import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Row,
    Select,
    Space,
    Tag,
    Typography
} from 'antd';
import {
    ShopOutlined,
    BarcodeOutlined,
    PhoneOutlined,
    MailOutlined,
    CheckCircleOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import PageTitle from '../../../components/common/PageTitle';
import {
    branchOfficeMaster
} from '../../../utility/mockData';
import { getBrand, getModel, getProductType } from '../../../redux/common/actionCreator';
import { PushLeadWrapper } from './style';

const { Text, Title } = Typography;

const toArray = (value) => (Array.isArray(value) ? value : []);

const findById = (list, id) => toArray(list).find((item) => String(item.id) === String(id));

const PushLead = () => {
    const config = {
        title: 'Push Lead',
        subtitle: 'Create and submit a lead for follow-up.',
    };
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const productTypeState = useSelector((state) => state.productType || {});
    const brandState = useSelector((state) => state.getBrand || {});
    const modelState = useSelector((state) => state.getModel || {});

    const productList = toArray(productTypeState.data);
    const brandList = toArray(brandState.data);
    const modelList = toArray(modelState.data);

    const [leadAllocationType, setLeadAllocationType] = useState('select_store'); // 'select_store', 'pincode'
    const [leadPincode, setLeadPincode] = useState(null);
    const [showPincodeStoreSelect, setShowPincodeStoreSelect] = useState(false);
    const [leadStore, setLeadStore] = useState(null);
    const [leadProductType, setLeadProductType] = useState(null);
    const [leadBrand, setLeadBrand] = useState(null);
    const [leadModel, setLeadModel] = useState(null);
    const [isLeadSubmitted, setIsLeadSubmitted] = useState(false);
    const [submittedLeadData, setSubmittedLeadData] = useState(null);
    const [, setFormValues] = useState({
        customerName: '',
        customerMobile: '',
        customerEmail: '',
        customerAddress: '',
        notes: ''
    });


    // --- PUSH LEAD COMPONENT LOGIC & HOOKS ---
    useEffect(() => {
        dispatch(getProductType());
        dispatch(getBrand());
    }, [dispatch]);

    useEffect(() => {
        if (!leadProductType || !leadBrand) return;

        dispatch(getModel({
            product_id: leadProductType,
            brand_id: leadBrand,
        }));
    }, [dispatch, leadBrand, leadProductType]);

    const filteredStoresForLead = useMemo(() => {
        if (leadAllocationType === 'pincode' && leadPincode) {
            const pin = leadPincode.trim();
            if (pin === '560095') return branchOfficeMaster.filter(b => b.branchCode === 'KOR-001');
            if (pin === '560038') return branchOfficeMaster.filter(b => b.branchCode === 'IND-002');
            if (pin === '560066') return branchOfficeMaster.filter(b => b.branchCode === 'WHF-003');
            if (pin === '560011') return branchOfficeMaster.filter(b => b.branchCode === 'JAY-004');
            if (pin === '560003') return branchOfficeMaster.filter(b => b.branchCode === 'MAL-005');
            return [];
        }
        return branchOfficeMaster;
    }, [leadAllocationType, leadPincode]);

    React.useEffect(() => {
        if (leadAllocationType === 'pincode' && filteredStoresForLead.length === 1) {
            setLeadStore(filteredStoresForLead[0].branchCode);
        }
    }, [filteredStoresForLead, leadAllocationType]);

    const selectedStoreObj = useMemo(() => {
        return branchOfficeMaster.find(b => b.branchCode === leadStore);
    }, [leadStore]);

    const productTypeOptions = useMemo(() => productList.map((item) => ({
        label: item.product_name,
        value: item.id,
    })), [productList]);

    const brandOptions = useMemo(() => brandList.map((item) => ({
        label: item.brand_name,
        value: item.id,
    })), [brandList]);

    const modelOptions = useMemo(() => modelList.map((item) => ({
        label: item.model_name,
        value: item.id,
        model_code: item.model_code,
    })), [modelList]);

    const selectedProductTypeObj = useMemo(() => findById(productList, leadProductType), [leadProductType, productList]);
    const selectedBrandObj = useMemo(() => findById(brandList, leadBrand), [brandList, leadBrand]);
    const selectedModelObj = useMemo(() => findById(modelList, leadModel), [leadModel, modelList]);


    // --- PUSH LEAD COMPONENT LOGIC & RENDER ---
    const handleLeadProductTypeChange = (productTypeId) => {
        setLeadProductType(productTypeId ?? null);
        setLeadModel(null);
    };

    const handleLeadBrandChange = (brandId) => {
        setLeadBrand(brandId ?? null);
        setLeadModel(null);
    };

    const handleLeadModelChange = (modelId) => {
        setLeadModel(modelId ?? null);
    };

    const handleLeadSubmit = (values) => {
        const leadId = `LD-2026-${Math.floor(1000 + Math.random() * 9000)}`;
        const selectedStoreObj = branchOfficeMaster.find(b => b.branchCode === leadStore);

        setSubmittedLeadData({
            leadId,
            customerName: values.customerName,
            customerMobile: values.customerMobile,
            customerEmail: values.customerEmail || 'N/A',
            customerAddress: values.customerAddress || 'N/A',
            allocationType: leadAllocationType === 'pincode' ? 'Pincode Search' : 'Select Store',
            pincode: leadPincode || 'N/A',
            storeName: selectedStoreObj ? selectedStoreObj.branchName : 'Unknown Store',
            storeCode: leadStore,
            productType: selectedProductTypeObj?.product_name || 'N/A',
            brand: selectedBrandObj?.brand_name || 'N/A',
            model: selectedModelObj?.model_name || 'N/A',
            modelCode: selectedModelObj?.model_code || 'N/A',
            notes: values.notes || 'No remarks provided.'
        });
        setIsLeadSubmitted(true);
    };

    const handleResetLeadForm = () => {
        form.resetFields();
        setIsLeadSubmitted(false);
        setSubmittedLeadData(null);
        setLeadAllocationType('select_store');
        setLeadPincode('');
        setLeadStore(null);
        setLeadProductType(null);
        setLeadBrand(null);
        setLeadModel(null);
        setFormValues({
            customerName: '',
            customerMobile: '',
            customerEmail: '',
            customerAddress: '',
            notes: ''
        });
    };

    return (
        <PushLeadWrapper>
            {isLeadSubmitted && submittedLeadData ? (
                <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '12px' }} />
                        <Title level={2} style={{ margin: 0 }}>Lead Pushed Successfully!</Title>
                        <Text type="secondary">New CRM Lead registration created. Reference ID: <Text strong>{submittedLeadData.leadId}</Text></Text>
                    </div>

                    <div className="lead-preview-ticket" style={{ border: '2px solid #52c41a' }}>
                        <div className="ticket-header" style={{ background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)' }}>
                            <Text strong style={{ color: '#ffffff', letterSpacing: '1px', fontSize: '11px', display: 'block' }}>ACTIVE CRM LEAD TICKET</Text>
                            <Title level={3} style={{ color: '#ffffff', margin: '4px 0 0 0' }}>{submittedLeadData.leadId}</Title>
                        </div>

                        <div className="ticket-body">
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer Information</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32', fontWeight: 'bold' }}>
                                            {submittedLeadData.customerName.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <Title level={5} style={{ margin: 0 }}>{submittedLeadData.customerName}</Title>
                                            <Space size="middle" style={{ marginTop: '2px' }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}><PhoneOutlined /> {submittedLeadData.customerMobile}</Text>
                                                {submittedLeadData.customerEmail !== 'N/A' && (
                                                    <Text type="secondary" style={{ fontSize: '12px' }}><MailOutlined /> {submittedLeadData.customerEmail}</Text>
                                                )}
                                            </Space>
                                        </div>
                                    </div>
                                </Col>

                                {submittedLeadData.customerAddress !== 'N/A' && (
                                    <Col span={24}>
                                        <Text type="secondary" style={{ fontSize: '10px' }}>Address</Text>
                                        <div style={{ marginTop: '2px' }}><Text>{submittedLeadData.customerAddress}</Text></div>
                                    </Col>
                                )}

                                <div className="ticket-divider" style={{ width: '100%' }}></div>

                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Allocated Branch Store</Text>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '6px' }}>
                                        <ShopOutlined style={{ color: '#2e7d32', marginTop: '4px' }} />
                                        <div>
                                            <Text strong style={{ display: 'block' }}>{submittedLeadData.storeName}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Code: {submittedLeadData.storeCode}</Text>
                                            {submittedLeadData.pincode !== 'N/A' && (
                                                <Tag color="green" style={{ marginTop: '4px' }}>PIN: {submittedLeadData.pincode}</Tag>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Product Interest</Text>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '6px' }}>
                                        <BarcodeOutlined style={{ color: '#2e7d32', marginTop: '4px' }} />
                                        <div>
                                            <Text strong style={{ display: 'block' }}>{submittedLeadData.brand} {submittedLeadData.model}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Product Type: {submittedLeadData.productType}</Text>
                                            {submittedLeadData.modelCode !== 'N/A' && (
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Model Code: {submittedLeadData.modelCode}</Text>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                <div className="ticket-divider" style={{ width: '100%' }}></div>

                                <Col span={24}>
                                    <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remarks / Lead Notes</Text>
                                    <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', marginTop: '6px', borderLeft: '3px solid #2e7d32' }}>
                                        <Text italic style={{ color: '#555' }}>"{submittedLeadData.notes}"</Text>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
                        <Button
                            size="large"
                            icon={<UndoOutlined />}
                            onClick={handleResetLeadForm}
                            style={{ borderRadius: '6px' }}
                        >
                            Push Another Lead
                        </Button>
                    </div>
                </div>
            ) : (
                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '12px 0' }}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleLeadSubmit}
                    >
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            <Card
                                title={
                                    <div className="custom-card-header">
                                        <span>Customer Details</span>
                                    </div>
                                }
                                className="custom-form-card"
                            >
                                <Row gutter={[24, 16]}>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Customer Name"
                                            name="customerName"
                                            rules={[{ required: true, message: 'Please enter customer name' }]}
                                        >
                                            <Input placeholder="Customer Name" size="large" style={{ borderRadius: '6px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Customer Mobile"
                                            name="customerMobile"
                                            rules={[
                                                { required: true, message: 'Please enter mobile number' },
                                                { pattern: /^\d{10}$/, message: 'Mobile number must be exactly 10 digits' }
                                            ]}
                                        >
                                            <Input placeholder="Customer Mobile" maxLength={10} size="large" style={{ borderRadius: '6px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Customer Email"
                                            name="customerEmail"
                                            rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                                        >
                                            <Input placeholder="Customer Email" size="large" style={{ borderRadius: '6px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item label="Customer Address" name="customerAddress">
                                            <Input.TextArea rows={3} placeholder="Customer Address" style={{ borderRadius: '6px' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card
                                title={
                                    <div className="custom-card-header">
                                        <span>Allocate Store</span>
                                    </div>
                                }
                                className="custom-form-card"
                            >
                                <Row gutter={[24, 16]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Allocation Type" required>
                                            <Select
                                                value={leadAllocationType}
                                                onChange={(val) => {
                                                    setLeadAllocationType(val);
                                                    setLeadPincode(null);
                                                    setLeadStore(null);
                                                    setShowPincodeStoreSelect(false);
                                                }}
                                                size="large"
                                                style={{ width: '100%', borderRadius: '6px' }}
                                            >
                                                <Select.Option value="select_store">Select Store</Select.Option>
                                                <Select.Option value="pincode">Pincode Search</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    {leadAllocationType === 'pincode' ? (
                                        <>
                                            <Col xs={24} md={12}>
                                                <Form.Item label="Search Store Pincode" required>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <Input
                                                            placeholder="Search Pincode"
                                                            maxLength={6}
                                                            value={leadPincode || ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/\D/g, '');
                                                                setLeadPincode(val);
                                                                setLeadStore(null);
                                                                setShowPincodeStoreSelect(false);
                                                            }}
                                                            size="large"
                                                            style={{ flex: 1, borderRadius: '6px' }}
                                                        />
                                                    </div>
                                                </Form.Item>
                                            </Col>

                                            {showPincodeStoreSelect && (
                                                <Col xs={24} md={12}>
                                                    <Form.Item label="Select Store" required>
                                                        <Select
                                                            placeholder="Choose branch store"
                                                            value={leadStore || undefined}
                                                            onChange={setLeadStore}
                                                            style={{ width: '100%', borderRadius: '6px' }}
                                                            showSearch
                                                            optionFilterProp="children"
                                                            size="large"
                                                        >
                                                            {filteredStoresForLead.map(b => (
                                                                <Select.Option key={b.branchCode} value={b.branchCode}>
                                                                    {b.branchName} ({b.branchCode})
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            )}
                                        </>
                                    ) : (
                                        <Col xs={24} md={12}>
                                            <Form.Item label="Select Store" required>
                                                <Select
                                                    placeholder="Choose branch store"
                                                    value={leadStore || undefined}
                                                    onChange={setLeadStore}
                                                    style={{ width: '100%', borderRadius: '6px' }}
                                                    showSearch
                                                    optionFilterProp="children"
                                                    size="large"
                                                >
                                                    {branchOfficeMaster.map(b => (
                                                        <Select.Option key={b.branchCode} value={b.branchCode}>
                                                            {b.branchName} ({b.branchCode})
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    )}

                                    {selectedStoreObj && (
                                        <Col xs={24}>
                                            <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                                                <div className="custom-card-header" style={{ marginBottom: '16px' }}>
                                                    <span>Store Details</span>
                                                </div>
                                                <Row gutter={[24, 16]}>
                                                    <Col xs={24} md={8}>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Name : <strong style={{ color: '#262626' }}>{selectedStoreObj.storeCode || 'KOR'}</strong>
                                                        </div>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Email : <strong style={{ color: '#262626' }}>{selectedStoreObj.email}</strong>
                                                        </div>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Sales Head : <strong style={{ color: '#262626' }}>{selectedStoreObj.salesHead || 'Santhosh B-6224'}</strong>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Name : <strong style={{ color: '#262626' }}>{selectedStoreObj.branchName}</strong>
                                                        </div>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Address : <strong style={{ color: '#262626' }}>{selectedStoreObj.address}</strong>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store Mobile : <strong style={{ color: '#262626' }}>{selectedStoreObj.phone}</strong>
                                                        </div>
                                                        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#595959' }}>
                                                            Store ASM : <strong style={{ color: '#262626' }}>{selectedStoreObj.asm || 'Ravikumar C N-6530'}</strong>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                    )}
                                </Row>

                                <div style={{ margin: '16px 0', borderTop: '1px solid #f0f0f0' }}></div>

                                <Row gutter={[24, 16]}>
                                    <Col xs={24} md={8}>
                                        <Form.Item label="Product Type" required>
                                            <Select
                                                placeholder="Select Product Type"
                                                value={leadProductType}
                                                onChange={handleLeadProductTypeChange}
                                                allowClear
                                                showSearch
                                                size="large"
                                                style={{ width: '100%', borderRadius: '6px' }}
                                                options={productTypeOptions}
                                                loading={productTypeState.loading}
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item label="Brand" required>
                                            <Select
                                                placeholder="Select Brand"
                                                value={leadBrand}
                                                onChange={handleLeadBrandChange}
                                                allowClear
                                                showSearch
                                                size="large"
                                                style={{ width: '100%', borderRadius: '6px' }}
                                                options={brandOptions}
                                                loading={brandState.loading}
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={8}>
                                        <Form.Item label="Model" required>
                                            <Select
                                                placeholder="Select Model"
                                                value={leadModel}
                                                onChange={handleLeadModelChange}
                                                allowClear
                                                showSearch
                                                size="large"
                                                style={{ width: '100%', borderRadius: '6px' }}
                                                disabled={!leadProductType || !leadBrand}
                                                options={modelOptions}
                                                loading={modelState.loading}
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card
                                title={
                                    <div className="custom-card-header">
                                        <span>Additional Information</span>
                                    </div>
                                }
                                className="custom-form-card"
                            >
                                <Form.Item name="notes" style={{ margin: 0 }}>
                                    <Input.TextArea rows={4} placeholder="Notes" style={{ borderRadius: '6px' }} />
                                </Form.Item>
                            </Card>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                                <Button onClick={handleResetLeadForm} size="large" style={{ borderRadius: '6px' }}>
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    style={
                                        (!leadStore || !leadProductType || !leadBrand || !leadModel)
                                            ? { borderRadius: '6px' }
                                            : { background: '#175AA0', borderColor: '#175AA0', borderRadius: '6px' }
                                    }
                                    disabled={!leadStore || !leadProductType || !leadBrand || !leadModel}
                                >
                                    Push Lead
                                </Button>
                            </div>
                        </Space>
                    </Form>
                </div>
            )}
        </PushLeadWrapper>
    );
};

export default PushLead;
